import React from 'react';
import styled from 'styled-components';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import { withLocalePrefix } from 'shared/utils/translation';
import { useTalentMatchingTabContext } from '../../contexts/talent_matching_tab_context';
import type { TalentMatchingFilters } from '../../types';
import ReviewStageDropdown from '../shared/review_stage_dropdown';

const t = withLocalePrefix('talent_matching.header_row.stage_picker');

// The table area clips overflow and the picker sits on its top-left edge, so draw
// the field's outline inside it rather than outside where it would be cut off.
const StagePickerWrapper = styled.div`
  width: 320px;

  .MuiInputBase-root {
    outline-offset: -1px;
  }
`;

const StagePicker = () => {
  const { reviewStages, selectedStageId, setSelectedStageId } =
    useTalentMatchingTabContext();
  const { setValue } = useFormContext<TalentMatchingFilters>();

  // With one stage there's nothing to pick, but a lone Hiring Manager Review stage still needs
  // naming, since the tab reads Application Review. A lone Application Review stage stays unchanged.
  if (reviewStages.length < 2) {
    const [onlyStage] = reviewStages;
    if (onlyStage?.kind !== 'hiring_manager_review') {
      return null;
    }

    return (
      <Body data-provides="talent-matching-stage-label">
        {t('single_stage', { stageName: onlyStage.name })}
      </Body>
    );
  }

  const handleChange = (stageId: number) => {
    if (stageId === selectedStageId) {
      return;
    }

    // Exclusions and the page belong to the previous stage's list.
    setValue('excludedIds', []);
    setValue('page', 1);
    setSelectedStageId(stageId);
  };

  return (
    <StagePickerWrapper>
      <ReviewStageDropdown
        data-provides="talent-matching-stage-picker"
        ariaLabel={t('label')}
        reviewStages={reviewStages}
        selectedStageId={selectedStageId}
        onChange={handleChange}
      />
    </StagePickerWrapper>
  );
};

export default StagePicker;
